import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUserRoles } from '@features/user';
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ForkLeft as ForkIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { hasRole } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  rulesApi,
  useDisableRuleMutation,
  useEnableRuleMutation,
  useForkRuleMutation,
  useGetRuleRevisionsQuery,
  useRevertRuleMutation,
} from '../store/api';
import type { ComplianceRuleDocument, LearningRuleDocument, ProviderRuleResponse } from '../types';

import { ComplianceRuleCard, LearningRuleCard } from './index';

interface RuleDetailPanelProps {
  rule: ProviderRuleResponse | null;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Whether this is being used in admin context (allows editing global rules) */
  isAdmin?: boolean;
}

export const RuleDetailPanel: React.FC<RuleDetailPanelProps> = ({
  rule,
  onClose,
  onEdit,
  onDelete,
  isAdmin = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const userRoles = useUserRoles();
  const isPracticeAdmin = hasRole(userRoles, 'practice_administrator');
  const [showRevisions, setShowRevisions] = useState(false);

  const [disableRule, { isLoading: isDisabling }] = useDisableRuleMutation();
  const [enableRule, { isLoading: isEnabling }] = useEnableRuleMutation();
  const [forkRule, { isLoading: isForking }] = useForkRuleMutation();
  const [revertRule, { isLoading: isReverting }] = useRevertRuleMutation();

  const { data: revisionsData } = useGetRuleRevisionsQuery(
    { ruleId: rule?.rule_id || '' },
    {
      skip: !rule || !showRevisions,
    },
  );

  if (!rule) return null;

  const isDisabled = rule.is_disabled ?? false;
  // In admin mode, allow editing/deleting global rules; otherwise only org rules
  const canEdit = isAdmin ? onEdit : !rule.is_global && onEdit;
  const canDelete = isAdmin ? onDelete : !rule.is_global && onDelete;
  const canDisable = (isPracticeAdmin || isAdmin) && rule.rule_type === 'compliance' && !isDisabled;
  const canEnable = (isPracticeAdmin || isAdmin) && rule.rule_type === 'compliance' && isDisabled;
  const canFork = (isPracticeAdmin || isAdmin) && rule.is_global && rule.rule_type === 'compliance';
  const hasRevisions = revisionsData && revisionsData.total > 0;

  const handleDisable = async () => {
    if (!rule) return;

    try {
      await disableRule({
        ruleId: rule.rule_id,
        providerRuleDisableRequest: {
          scope: 'org',
        },
      }).unwrap();

      // Invalidate cache to refresh rule list, rule details, and search results
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: rule.rule_id },
          { type: 'RuleList', id: 'LIST' },
          { type: 'RuleSearch', id: 'LIST' },
        ]),
      );
      enqueueSnackbar('Rule disabled successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to disable rule:', error);
      enqueueSnackbar('Failed to disable rule', { variant: 'error' });
    }
  };

  const handleEnable = async () => {
    if (!rule) return;

    try {
      await enableRule({
        ruleId: rule.rule_id,
        providerRuleDisableRequest: {
          scope: 'org',
        },
      }).unwrap();

      // Invalidate cache to refresh rule list, rule details, and search results
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: rule.rule_id },
          { type: 'RuleList', id: 'LIST' },
          { type: 'RuleSearch', id: 'LIST' },
        ]),
      );
      enqueueSnackbar('Rule enabled successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to enable rule:', error);
      enqueueSnackbar('Failed to enable rule', { variant: 'error' });
    }
  };

  const handleFork = async () => {
    if (!rule) return;

    try {
      const result = await forkRule({
        ruleId: rule.rule_id,
        providerRuleForkRequest: {
          target_scope: 'org',
          auto_disable_source: true,
        },
      }).unwrap();

      // Invalidate cache to refresh rule list, rule details, and search results (fork creates a new rule and may disable source)
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: rule.rule_id },
          { type: 'RuleList', id: 'LIST' },
          { type: 'RuleSearch', id: 'LIST' },
        ]),
      );

      enqueueSnackbar(`Rule forked successfully. New rule ID: ${result.new_rule_id}`, {
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to fork rule:', error);
      enqueueSnackbar('Failed to fork rule', { variant: 'error' });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Rule Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: 'background.default' }}>
        <Box>
          {rule.rule_type === 'compliance' ? (
            <ComplianceRuleCard
              rule={rule.rule as ComplianceRuleDocument}
              isGlobal={rule.is_global}
              isDisabled={isDisabled}
              variant="full"
            />
          ) : (
            <LearningRuleCard
              rule={rule.rule as LearningRuleDocument}
              isGlobal={rule.is_global}
              variant="full"
            />
          )}
          {showRevisions && revisionsData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Revision History ({revisionsData.total})
              </Typography>
              <Stack spacing={1}>
                {revisionsData.revisions?.map((revision, index) => (
                  <Paper key={revision.version} sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Version {revision.version}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(revision.created_at).toLocaleString()} by {revision.created_by}
                    </Typography>
                    {isPracticeAdmin && index > 0 && (
                      <Button
                        size="small"
                        onClick={async () => {
                          try {
                            await revertRule({
                              ruleId: rule.rule_id,
                              providerRuleRevertRequest: {
                                version: revision.version,
                              },
                            }).unwrap();

                            // Invalidate cache to refresh rule details, list, and search results
                            dispatch(
                              rulesApi.util.invalidateTags([
                                { type: 'Rule', id: rule.rule_id },
                                { type: 'RuleList', id: 'LIST' },
                                { type: 'RuleSearch', id: 'LIST' },
                              ]),
                            );
                            enqueueSnackbar(`Reverted to version ${revision.version}`, {
                              variant: 'success',
                            });
                            setShowRevisions(false);
                          } catch (error) {
                            console.error('Failed to revert rule:', error);
                            enqueueSnackbar('Failed to revert rule', { variant: 'error' });
                          }
                        }}
                        disabled={isReverting}
                      >
                        Revert to this version
                      </Button>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer/Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          {hasRevisions && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setShowRevisions(!showRevisions)}
            >
              {showRevisions ? 'Hide' : 'Show'} History
            </Button>
          )}
          {canFork && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ForkIcon />}
              onClick={handleFork}
              disabled={isForking}
            >
              {isForking ? 'Forking...' : 'Fork Rule'}
            </Button>
          )}
          {canEnable && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleEnable}
              disabled={isEnabling}
            >
              {isEnabling ? 'Enabling...' : 'Enable Rule'}
            </Button>
          )}
          {canDisable && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<BlockIcon />}
              onClick={handleDisable}
              disabled={isDisabling}
            >
              {isDisabling ? 'Disabling...' : 'Disable Rule'}
            </Button>
          )}
          {canDelete && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
              Delete
            </Button>
          )}
          {canEdit && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>
              Edit Rule
            </Button>
          )}
          {!canEdit && rule.is_global && !isAdmin && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              disabled
              title="Global rules cannot be edited (admin access required)"
            >
              Edit Rule
            </Button>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};
